import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How much does flooring installation cost per square foot?",
      answer: "Flooring installation costs vary depending on the type of flooring you choose. Laminate typically ranges from $3-8 per sq ft, and engineered hardwood from $8-15 per sq ft. This includes both materials and professional installation. Contact us for a free, no-obligation quote tailored to your specific project."
    },
    {
      question: "Do you offer free quotes?",
      answer: "Yes! We provide free, no-obligation quotes for all flooring projects. Simply fill out our Request Quote form or call us at 604-616-3986. We'll schedule an in-home consultation to assess your space, discuss your preferences, and provide a detailed estimate."
    },
    {
      question: "What areas do you serve?",
      answer: "We proudly serve customers throughout British Columbia, including Richmond, Coquitlam, Port Coquitlam, Port Moody, Burnaby, Vancouver, Surrey, Maple Ridge, and the entire Lower Mainland, Fraser Valley, Metro Vancouver, and Tri-Cities area."
    },
    {
      question: "How long does flooring installation take?",
      answer: "Installation time depends on the size of your space and the type of flooring. A typical residential room (200-400 sq ft) can be completed in 1-3 days. Larger projects or whole-home installations may take 5-7 days. We'll provide a detailed timeline during your free consultation."
    },
    {
      question: "What is the difference between laminate and hardwood flooring?",
      answer: "Laminate is a durable, affordable option with a wood-look surface layer that offers excellent water resistance and easy maintenance. Engineered hardwood offers the authentic look and feel of real wood with better moisture resistance than solid hardwood. Each has unique benefits, and we can help you choose the best option for your needs and budget."
    },
    {
      question: "Is your flooring water-resistant?",
      answer: "Our ComfortSense laminate collection features water-resistant flooring, ideal for high-traffic areas. Our NatureSense engineered hardwood also offers excellent moisture resistance. We'll recommend the best option based on your specific room requirements."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    script.id = 'faq-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('faq-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="w-24 h-1 bg-luxury-gold mx-auto mb-6"></div>
          <h2 className="text-5xl md:text-6xl font-bold text-luxury-navy mb-6 luxury-heading">
            Frequently Asked Questions
          </h2>
          <p className="text-luxury-slate max-w-2xl mx-auto text-xl md:text-2xl">
            Got questions about flooring? We've got answers. If you don't see your question here, feel free to contact us!
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <article
              key={index}
              className="bg-white rounded-sm border-l-4 border-luxury-gold/30 hover:border-luxury-gold shadow-lg hover:shadow-2xl overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-300"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-xl md:text-2xl font-semibold text-luxury-navy pr-4">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-luxury-gold flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-8 py-5 bg-gradient-to-br from-gray-50 to-white border-t border-luxury-gold/20">
                  <p className="text-luxury-slate leading-relaxed text-lg">{faq.answer}</p>
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-luxury-slate mb-6 text-lg">Still have questions?</p>
          <a
            href="tel:+16046163986"
            className="inline-flex items-center bg-luxury-gold text-luxury-navy px-10 py-4 rounded-sm font-semibold hover:bg-luxury-gold-dark transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 group"
          >
            <span>Call Us: 604-616-3986</span>
            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
